import { useRegisterUser } from '@/features/users/hooks';
import { FIELD_MAX_LENGTHS } from '@/shared/constants';
import { useAppForm } from '@/shared/lib/form';
import { Show } from '@/shared/ui';
import { normalizeEmail, validateCpf, validatePhone } from '@/shared/utils';
import {
  Button,
  Dialog,
  Field,
  RadioCard,
  Select,
} from '@noorden-insurtech/alba-design-system';
import { revalidateLogic } from '@tanstack/react-form';
import { Building, User, UserPlus, Users } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';
import { RegisterUserPayload } from '../../../services';
import { toaster } from '@/shared/ui/toaster.component';
import {
  getRoleOptionsByUserType,
  getUserGroup,
  UserRole,
} from '@/shared/utils/get-group';
import { removeFormatPhone } from '@/shared/utils/remove-format-phone';

interface CreateAdvisoryUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  advisoryId: string;
  advisoryName: string;
}

const roleOptions = getRoleOptionsByUserType('advisory');

const baseSchema = z.object({
  name: z.string().min(1, 'Nome é obrigatório'),
  cpf: z.string().refine(v => validateCpf(v), 'CPF inválido'),
  email: z.email('E-mail inválido'),
  phone: z.string().refine(v => validatePhone(v), 'Telefone inválido'),
  role: z.string().min(1, 'Perfil é obrigatório'),
  advisoryId: z.string(),
  userType: z.enum(['insurer', 'advisory', 'broker']),
});

const schemaWithUserType = baseSchema.refine(
  v => v.userType !== 'advisory' || !!v.advisoryId,
  {
    message: 'Assessoria é obrigatória',
    path: ['advisoryId'],
  }
);

export const CreateAdvisoryUserModal = ({
  isOpen,
  onClose,
  onSuccess,
  advisoryId,
  advisoryName,
}: CreateAdvisoryUserModalProps) => {
  const [userType, setUserType] = useState<'insurer' | 'advisory' | 'broker'>(
    'advisory'
  );

  const { mutateAsync, isPending: isRegisteringUser } = useRegisterUser();

  const { AppField, handleSubmit, reset } = useAppForm({
    defaultValues: {
      name: '',
      cpf: '',
      email: '',
      phone: '',
      role: '',
      advisoryId: advisoryName,
      userType: 'advisory',
    },
    onSubmit: async () => {},
    validationLogic: revalidateLogic(),
    validators: {
      onChange: schemaWithUserType,
      onSubmitAsync: async ({ value }) => {
        const { name, cpf, email, phone, role } = value as Record<
          string,
          string
        >;
        if (!name || !email || !role) return null;
        if (userType === 'advisory' && !advisoryId) return null;

        const payload: RegisterUserPayload = {
          email: normalizeEmail(email),
          name,
          partyId: advisoryId,
          customAttributes: {
            'custom:group': getUserGroup('advisory', role as UserRole),
            'custom:contact_number': removeFormatPhone(phone) || undefined,
            'custom:register_number': cpf || undefined,
            'custom:profile': userType,
            'custom:party_type': '2',
            'custom:advisory_id': advisoryId,
          },
        };

        try {
          await mutateAsync(payload);
          toaster.create({
            type: 'success',
            title: 'Usuário adicionado',
            description: 'Usuário adicionado à assessoria com sucesso.',
          });
          onSuccess?.();
          return null;
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Erro ao criar usuário';
          const msg = errorMessage.trim();
          const lower = msg.toLowerCase();

          toaster.create({
            type: 'error',
            title: 'Erro ao adicionar usuário',
            description: msg,
          });

          const fields: Record<string, { message: string } | undefined> = {};

          if (lower.includes('cpf')) {
            fields['cpf'] = { message: msg };
          }

          if (
            lower.includes('email') ||
            lower.includes('already exists') ||
            lower.includes('já cadastrado')
          ) {
            fields['email'] = {
              message: lower.includes('email')
                ? msg
                : 'Este e-mail já está cadastrado em outro usuário.',
            };
          }

          return { fields } as const;
        }
      },
    },
  });

  return (
    <Dialog.Root open={isOpen} onOpenChange={() => onClose()}>
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content className='w-[calc(100vw-1.5rem)] max-w-3xl max-h-[calc(100vh-1.5rem)] overflow-hidden'>
          <Dialog.Header>
            <Dialog.Title>
              <div className='flex items-center gap-3'>
                <UserPlus className='h-6 w-6' />
                Adicionar Usuário à Assessoria
              </div>
            </Dialog.Title>
            <Dialog.CloseButton onClick={() => onClose()} />
          </Dialog.Header>
          <Dialog.Body className='overflow-y-auto px-4 sm:px-6'>
            <div className='flex flex-col gap-6'>
              <div className='flex flex-col gap-3'>
                <AppField name='userType'>
                  {field => (
                    <RadioCard.Root
                      value={
                        (field.state.value || userType) as
                          | 'insurer'
                          | 'advisory'
                          | 'broker'
                      }
                      onValueChange={details => {
                        const next = details.value as
                          | 'insurer'
                          | 'advisory'
                          | 'broker';
                        setUserType(next);
                        field.handleChange(next);
                      }}
                    >
                      <RadioCard.Label>Tipo de Usuário *</RadioCard.Label>
                      <div className='grid grid-cols-1 gap-4 md:grid-cols-3'>
                        <RadioCard.Item value='insurer' disabled>
                          <RadioCard.ItemControl>
                            <RadioCard.ItemIcon>
                              <Building />
                            </RadioCard.ItemIcon>
                            <RadioCard.RadioButton />
                          </RadioCard.ItemControl>
                          <RadioCard.ItemContent>
                            <RadioCard.ItemText>Seguradora</RadioCard.ItemText>
                            <RadioCard.ItemDescription>
                              Usuários internos da seguradora
                            </RadioCard.ItemDescription>
                          </RadioCard.ItemContent>
                          <RadioCard.ItemHiddenInput />
                        </RadioCard.Item>
                        <RadioCard.Item value='advisory' disabled>
                          <RadioCard.ItemControl>
                            <RadioCard.ItemIcon>
                              <Users />
                            </RadioCard.ItemIcon>
                            <RadioCard.RadioButton />
                          </RadioCard.ItemControl>
                          <RadioCard.ItemContent>
                            <RadioCard.ItemText>Assessoria</RadioCard.ItemText>
                            <RadioCard.ItemDescription>
                              Usuários de assessoria parceira
                            </RadioCard.ItemDescription>
                          </RadioCard.ItemContent>
                          <RadioCard.ItemHiddenInput />
                        </RadioCard.Item>
                        <RadioCard.Item value='broker' disabled>
                          <RadioCard.ItemControl>
                            <RadioCard.ItemIcon>
                              <User />
                            </RadioCard.ItemIcon>
                            <RadioCard.RadioButton />
                          </RadioCard.ItemControl>
                          <RadioCard.ItemContent>
                            <RadioCard.ItemText>Corretor</RadioCard.ItemText>
                            <RadioCard.ItemDescription>
                              Usuários de corretor parceiro
                            </RadioCard.ItemDescription>
                          </RadioCard.ItemContent>
                          <RadioCard.ItemHiddenInput />
                        </RadioCard.Item>
                      </div>
                    </RadioCard.Root>
                  )}
                </AppField>
              </div>

              <form
                onSubmit={async e => {
                  e.preventDefault();
                  e.stopPropagation();
                  await handleSubmit();
                }}
                className='grid grid-cols-1 gap-4 md:grid-cols-2'
              >
                <Show when={userType === 'advisory'}>
                  <div className='md:col-span-2'>
                    <AppField name='advisoryId'>
                      {field => (
                        <field.InputField
                          label='Assessoria Vinculada *'
                          required
                          type='text'
                          placeholder='Assessoria selecionada'
                          disabled={true}
                          helperText=''
                        />
                      )}
                    </AppField>
                  </div>
                </Show>
                <div className='md:col-span-2'>
                  <AppField name='name'>
                    {field => (
                      <field.InputField
                        label='Nome *'
                        helperText=''
                        type='text'
                        placeholder='Digite o nome do usuário'
                        required
                        maxLength={FIELD_MAX_LENGTHS.personName}
                        disabled={isRegisteringUser}
                      />
                    )}
                  </AppField>
                </div>

                <div className='md:col-span-2'>
                  <AppField name='cpf'>
                    {field => (
                      <field.InputField
                        label='CPF *'
                        required
                        helperText=''
                        type='text'
                        placeholder='XXX.XXX.XXX-XX'
                        maxLength={FIELD_MAX_LENGTHS.cpf}
                        mask='cpf'
                        disabled={isRegisteringUser}
                      />
                    )}
                  </AppField>
                </div>

                <div className='md:col-span-2'>
                  <AppField
                    name='email'
                    children={field => (
                      <field.InputField
                        label='E-mail'
                        required
                        helperText=''
                        mask='email'
                        type='email'
                        lowercase
                        placeholder='Digite o e-mail do usuário'
                        maxLength={FIELD_MAX_LENGTHS.email}
                        disabled={isRegisteringUser}
                      />
                    )}
                  />
                </div>

                <div className='grid grid-cols-1 gap-4 md:col-span-2 md:grid-cols-2'>
                  <AppField
                    name='role'
                    children={field => {
                      const showError =
                        !field.state.meta.isValid &&
                        (field.state.meta.isTouched ||
                          field.form.state.isSubmitted);

                      const errorText = field.state.meta.errors
                        ?.map(err => {
                          if (!err) return '';
                          if (typeof err === 'string') return err;
                          if (
                            typeof err === 'object' &&
                            'message' in err &&
                            typeof (err as { message?: unknown }).message ===
                              'string'
                          ) {
                            return (err as { message: string }).message;
                          }
                          return '';
                        })
                        .filter(Boolean)
                        .join(', ');

                      return (
                        <Field.Root required invalid={showError}>
                          <Field.Label className='text-alba-black-500'>
                            Perfil*
                          </Field.Label>
                          <Select.Root
                            items={roleOptions}
                            value={field.state.value ? [field.state.value] : []}
                            onValueChange={details =>
                              field.handleChange(details.value[0] || '')
                            }
                            onFocusOutside={field.handleBlur}
                            positioning={{ sameWidth: true }}
                            disabled={isRegisteringUser}
                          >
                            <Select.Control>
                              <Select.Trigger>
                                <Select.ValueText placeholder='Selecione o perfil' />
                                <Select.Indicator>
                                  <svg
                                    className='h-4 w-4'
                                    fill='none'
                                    stroke='currentColor'
                                    viewBox='0 0 24 24'
                                  >
                                    <path
                                      strokeLinecap='round'
                                      strokeLinejoin='round'
                                      strokeWidth={2}
                                      d='M19 9l-7 7-7-7'
                                    />
                                  </svg>
                                </Select.Indicator>
                              </Select.Trigger>
                            </Select.Control>
                            <Select.Portal>
                              <Select.Positioner>
                                <Select.Content>
                                  {roleOptions.map(option => (
                                    <Select.Item
                                      key={option.value}
                                      item={option}
                                    >
                                      <Select.ItemText>
                                        {option.label}
                                      </Select.ItemText>
                                      <Select.ItemIndicator>
                                        <svg
                                          className='h-4 w-4'
                                          viewBox='0 0 20 20'
                                          fill='currentColor'
                                        >
                                          <path
                                            fillRule='evenodd'
                                            d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                                            clipRule='evenodd'
                                          />
                                        </svg>
                                      </Select.ItemIndicator>
                                    </Select.Item>
                                  ))}
                                </Select.Content>
                              </Select.Positioner>
                            </Select.Portal>
                          </Select.Root>
                          {showError && !!errorText && (
                            <Field.ErrorText>{errorText}</Field.ErrorText>
                          )}
                        </Field.Root>
                      );
                    }}
                  />

                  <AppField
                    name='phone'
                    children={field => (
                      <field.InputField
                        label='Telefone'
                        helperText=''
                        type='tel'
                        mask='(99) 9{8,9}'
                        placeholder='(XX) XXXXXXXXX'
                        maxLength={FIELD_MAX_LENGTHS.phone}
                        required={false}
                        disabled={isRegisteringUser}
                      />
                    )}
                  />
                </div>
              </form>
            </div>
          </Dialog.Body>
          <Dialog.Footer className='flex flex-col-reverse gap-2 px-4 pb-4 sm:flex-row sm:justify-end sm:px-6'>
            <Button
              variant='ghost'
              className='w-full sm:w-auto'
              onClick={() => {
                reset();
                onClose();
              }}
            >
              Cancelar
            </Button>
            <Button
              loadingText='Salvando...'
              loading={isRegisteringUser}
              variant='primary'
              className='w-full sm:w-auto'
              onClick={async () => {
                await handleSubmit();
                reset();
              }}
            >
              Salvar
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
};
